import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { setRecentVerificationHandler } from "../../auth/recentVerification";
import { supabase } from "../../auth/supabase";

const CODE_TTL_SECONDS = 5 * 60;

export default function RecentVerificationDialog() {
  const resolver = useRef(null);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("continue");
  const [contacts, setContacts] = useState([]);
  const [contact, setContact] = useState(null);
  const [code, setCode] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const close = (verified = false) => {
    resolver.current?.(verified);
    resolver.current = null;
    setOpen(false);
    setContact(null);
    setCode("");
    setError("");
  };

  useEffect(() => {
    setRecentVerificationHandler(async (nextAction) => {
      const { data, error: userError } = await supabase.auth.getUser();
      if (userError) return false;
      const available = [
        data.user?.email && data.user.email_confirmed_at ? { kind: "email", value: data.user.email } : null,
        data.user?.phone && data.user.phone_confirmed_at ? { kind: "phone", value: data.user.phone } : null,
      ].filter(Boolean);
      if (!available.length) return false;
      setAction(nextAction);
      setContacts(available);
      setContact(null);
      setOpen(true);
      return new Promise((resolve) => { resolver.current = resolve; });
    });
    return () => setRecentVerificationHandler(null);
  }, []);

  useEffect(() => {
    if (!seconds) return undefined;
    const timer = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [seconds]);

  const sendCode = async (selected) => {
    setLoading(true);
    setError("");
    const { error: sendError } = await supabase.auth.signInWithOtp({
      ...(selected.kind === "email" ? { email: selected.value } : { phone: selected.value }),
      options: { shouldCreateUser: false },
    });
    setLoading(false);
    if (sendError) return setError("We couldn't send a verification code. Please sign in again or try later.");
    setContact(selected);
    setCode("");
    setSeconds(CODE_TTL_SECONDS);
  };

  const verify = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      ...(contact.kind === "email" ? { email: contact.value, type: "email" } : { phone: contact.value, type: "sms" }),
      token: code,
    });
    setLoading(false);
    if (verifyError || !data.session) return setError("That code is invalid or expired. Request a new code and try again.");
    close(true);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="recent-verification-title">
      <form className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900" onSubmit={verify}>
        <div className="flex items-start justify-between gap-4">
          <div><h2 id="recent-verification-title" className="text-xl font-semibold text-gray-900 dark:text-white">Confirm it’s you</h2><p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">Verify once to {action}. You won’t be asked again for sensitive changes for 10 minutes.</p></div>
          <button type="button" aria-label="Close" onClick={() => close(false)} className="rounded-lg px-2 py-1 text-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">×</button>
        </div>
        {!contact ? <div className="mt-5 space-y-3">{contacts.map((item) => <button key={`${item.kind}:${item.value}`} type="button" disabled={loading} onClick={() => sendCode(item)} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-800 hover:border-blue-500 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200"><span className="block text-xs uppercase tracking-wide text-gray-400">Send code to {item.kind}</span>{item.value}</button>)}</div> : <><input autoFocus required inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength="6" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="6-digit verification code" className="mt-5 w-full rounded-xl border border-gray-300 px-4 py-3 tracking-[0.35em] outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white" /><p className="mt-2 text-xs text-gray-500">Code expires in {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</p></>}
        {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
        {contact && <div className="mt-6 flex justify-end gap-3"><button type="button" disabled={loading || seconds > 270} onClick={() => sendCode(contact)} className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold disabled:opacity-50 dark:border-gray-700 dark:text-gray-200">Request new code</button><button type="submit" disabled={loading || code.length !== 6 || seconds <= 0} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{loading ? "Please wait…" : "Verify"}</button></div>}
      </form>
    </div>,
    document.body,
  );
}
